import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';

export default function LoginPage() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setError('');
  };

  const handleLogin = async () => {
    if (!form.email || !form.password) {
      setError('Please enter your email and password');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post('/api/auth/login', {
        email: form.email,
        password: form.password
      });

      // Store token and user info in localStorage
      // JWT token — proves user is logged in on every request
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));

      // Redirect to dashboard
      navigate('/dashboard');

    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  // Allow login on Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", boxSizing: "border-box" }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: rgba(148,163,184,0.4); }
      `}</style>

      <div style={{ width: "100%", maxWidth: 400, animation: "fadeIn 0.5s ease" }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <h1 aria-label="Smart Maji" style={{ margin: 0, display: "flex", justifyContent: "center" }}>
            <Logo height={56} tagline />
          </h1>
          <p style={{ color: "#475569", fontSize: 15, marginTop: 14 }}>
            Sign in to your account
          </p>
        </div>

        {/* Card */}
        <div style={{ background: "rgba(15,23,42,0.92)", backdropFilter: "blur(20px)", borderRadius: 20, border: "1px solid rgba(56,189,248,0.15)", boxShadow: "0 20px 50px rgba(0,0,0,0.5)", padding: "32px 28px" }}>

          {/* Email */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", color: "#94a3b8", fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Email</label>
            <div style={{ position: "relative" }}>
              <input
                type="email"
                value={form.email}
                onChange={e => handleChange('email', e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="you@example.com"
                style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: "1.5px solid rgba(56,189,248,0.25)", borderRadius: 10, padding: "13px 14px", color: "#e2e8f0", fontSize: 15, outline: "none", fontFamily: "inherit" }}
                onFocus={e => e.target.style.border = "1.5px solid #38bdf8"}
                onBlur={e => e.target.style.border = "1.5px solid rgba(56,189,248,0.25)"}
              />
            </div>
          </div>

          {/* Password */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", color: "#94a3b8", fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Password</label>
            <div style={{ position: "relative" }}>
              <input
                type="password"
                value={form.password}
                onChange={e => handleChange('password', e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter your password"
                style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: "1.5px solid rgba(56,189,248,0.25)", borderRadius: 10, padding: "13px 14px", color: "#e2e8f0", fontSize: 15, outline: "none", fontFamily: "inherit" }}
                onFocus={e => e.target.style.border = "1.5px solid #38bdf8"}
                onBlur={e => e.target.style.border = "1.5px solid rgba(56,189,248,0.25)"}
              />
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div style={{ background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 8, padding: "10px 14px", color: "#f87171", fontSize: 13, marginBottom: 16 }}>
              {error}
            </div>
          )}

          {/* Login button */}
          <button
            onClick={handleLogin}
            disabled={loading}
            style={{ width: "100%", background: loading ? "rgba(8,145,178,0.5)" : "linear-gradient(135deg, #0891b2, #0e7490)", border: "none", borderRadius: 12, padding: "14px", color: "#fff", fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 4px 20px rgba(8,145,178,0.35)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            {loading ? (
              <>
                <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid #fff", animation: "spin 1s linear infinite" }} />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>

          <button onClick={() => navigate('/forgot-password')}
            style={{ width: "100%", marginTop: 14, background: "none", border: "none", color: "#7cc4ff", fontSize: 14.5, fontWeight: 600, cursor: "pointer", padding: "6px" }}>
            Forgot your password?
          </button>

          {/* Back to home */}
          <button
            onClick={() => navigate('/')}
            style={{ width: "100%", marginTop: 12, background: "none", border: "none", color: "#64748b", fontSize: 13, cursor: "pointer", padding: "8px" }}
          >
            ← Back to Home
          </button>
        </div>

        <p style={{ textAlign: "center", color: "#334155", fontSize: 12, marginTop: 16 }}>
          © 2026 Majic Water Systems
        </p>
      </div>
    </div>
  );
}